import { FC, useMemo } from "react";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, Button, Grid, Stack, Tab } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { NavigateIcon } from "../../../assets/icons";
import { AppMenuKey, AppMenuSectionItem, AppRoutes } from "../../../common/routesUtils";
import DropdownSelect from "../../../components/common/DropdownSelect";
import { TransportPerspectiveSelector } from "../../../components/common/TransportPerspectiveSelector";
import { View } from "../../../components/layout/View";
import { PageType } from "../../../config/page.types";
import { useBreadcrumbs } from "../../../context/BreadcrumbsProvider";
import { useFiltersRegistry } from "../../../context/FiltersProvider";
import { usePageConfigurationState } from "../../../context/PageConfigrations/usePageConfigurationState";
import { EmissionsTransportResultsDataDTO } from "../../../datasources/api-client/data-contracts";
import { useNavigationPreferences } from "../../../hooks/usePreferences";
import Greenrouter from "./tabs/Greenrouter";
import Overview from "./tabs/Overview";
import ThirdParty from "./tabs/ThirdParty";
import { useTransportData } from "./useTransportData";

interface Props {
	menuKeys?: Array<AppMenuKey>;
	path?: Array<AppMenuSectionItem>;
}

export type EmissionDataKey = keyof EmissionsTransportResultsDataDTO;
// Keys for the dropdown select
type DropdownEmissionsKey = "co2Ttw" | "co2Wtw";

const ResultsTransport: FC<Props> = () => {
	const { t } = useTranslation(["result.transport", "common"]);

	const navigate = useNavigate();
	const { visitPage } = useBreadcrumbs();
	const { navigationPreferences } = useNavigationPreferences();

	const { appliedFilters } = useFiltersRegistry();

	// Page configuration, stored in localStorage by PageType
	const [config, setConfig] = usePageConfigurationState(PageType.ResultsTransport, {
		emissionsKey: "co2Ttw",
		tabValue: "overview",
		overviewEmissionsIntervalsPeriodType: "month",
		overviewIntervalMode: "emissions",
		greenrouterEmissionsMapSource: "trips-origin",
	});

	// Dropdown options for emission selection
	const dropdownSelectEmissions = [
		{ key: "co2Ttw", label: t("common:ttw select") },
		{ key: "co2Wtw", label: t("common:wtw select") },
	];

	// Update selected emission type in page config
	const handleEmissionChange = (key: string) => {
		setConfig({ emissionsKey: key as DropdownEmissionsKey });
	};

	const handleChange = (event: React.SyntheticEvent, newValue: "overview" | "greenrouter" | "thirdparty") => {
		setConfig({ tabValue: newValue });
	};

	/**
	 * Payload for data fetching
	 * Automatically re-memoized when filters or period change
	 */
	const payload = useMemo(() => ({ ...appliedFilters.payload }), [appliedFilters]);

	const { emissions, thirdParty, fileDownload } = useTransportData({
		payload,
		periodType: config.overviewEmissionsIntervalsPeriodType,
		perspective: navigationPreferences.transportsPerspective,
		tab: config.tabValue,
	});

	return (
		<View
			pageType={PageType.ResultsTransport}
			headerContext={{
				payload: { ...payload, perspective: navigationPreferences.transportsPerspective },
				fileDownload,
				t,
			}}
		>
			<TabContext value={config.tabValue}>
				<Grid container spacing={2}>
					<Grid size={12}>
						<Stack direction={"row"} spacing={2} justifyContent={"space-between"} alignItems={"center"}>
							<Stack direction={"row"} spacing={2}>
								<TabList onChange={handleChange} aria-label="Transport tabs">
									<Tab label="Overview" value="overview" sx={{ textTransform: "uppercase" }} />
									<Tab label="Greenrouter" value="greenrouter" sx={{ textTransform: "uppercase" }} />
									<Tab
										label={t("third parties")}
										value="thirdparty"
										sx={{ textTransform: "uppercase" }}
									/>
								</TabList>

								{config.tabValue === "greenrouter" && (
									<Button
										variant="contained"
										color="primary"
										onClick={() => {
											visitPage(PageType.ResultsTransportGreenRouter);
											navigate(AppRoutes.ResultsTransportGreenrouter);
										}}
										endIcon={<NavigateIcon color="inherit" />}
									>
										{t("view greenrouter transport")}
									</Button>
								)}

								{config.tabValue === "thirdparty" && (
									<Button
										variant="contained"
										color="primary"
										onClick={() => {
											visitPage(PageType.ResultsTransportThirdParty);
											navigate(AppRoutes.ResultsTransportThirdParty);
										}}
										endIcon={<NavigateIcon color="inherit" />}
									>
										{t("view third party transport")}
									</Button>
								)}
							</Stack>

							<Box sx={{ flexGrow: 1 }} />
							<DropdownSelect
								text={t("display for")}
								value={config.emissionsKey}
								loading={emissions.transport.isLoading}
								onChange={handleEmissionChange}
								dropdownItems={dropdownSelectEmissions}
								variant="standard"
							/>

							{config.tabValue !== "thirdparty" && <TransportPerspectiveSelector />}
						</Stack>
					</Grid>
					<TabPanel value="overview" sx={{ padding: 0, width: "100%" }}>
						<Overview
							transportEmissions={emissions.transport}
							keyEmissions={config.emissionsKey}
							emissionsIntervals={emissions.intervals}
							emissionsIntervalsPeriodType={config.overviewEmissionsIntervalsPeriodType}
							setEmissionsIntervalsPeriodType={(periodType) =>
								setConfig({ overviewEmissionsIntervalsPeriodType: periodType })
							}
							intervalMode={config.overviewIntervalMode}
							onIntervalModeChange={(intervalMode) => setConfig({ overviewIntervalMode: intervalMode })}
						/>
					</TabPanel>

					<TabPanel value="greenrouter" sx={{ padding: 0, width: "100%" }}>
						<Greenrouter
							transportEmissions={emissions.transport}
							keyEmissions={config.emissionsKey}
							emissionTypeTransport={emissions.typeTransport}
							emissionsChainTransport={emissions.chainTransport}
							selectedSource={config.greenrouterEmissionsMapSource}
							onSourceChange={(value) => setConfig({ greenrouterEmissionsMapSource: value })}
							perspective={navigationPreferences.transportsPerspective}
						/>
					</TabPanel>

					<TabPanel value="thirdparty" sx={{ padding: 0, width: "100%" }}>
						<ThirdParty
							keyEmissions={config.emissionsKey}
							transportEmissions={emissions.transport}
							calculationMethods={thirdParty.calculationMethods}
							nodesCertification={thirdParty.nodesCertification}
							environmentalKpi={thirdParty.environmentalKpi}
							typeTransport={thirdParty.typeTransport}
							chainsTransport={thirdParty.chainTransport}
						/>
					</TabPanel>
				</Grid>
			</TabContext>
		</View>
	);
};

export default ResultsTransport;
